import { createStyles, makeStyles } from '@material-ui/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import TextBlockEditModal from 'src/components/account/translations/TextBlockEditModal';
import { useAccount } from 'src/core/hooks/account';
import { setTranslatorMode, textBlockKeyForRenderedText, useTranslatorMode } from 'src/core/translatorMode';
import { isAdminContext } from 'src/core/utils';

// icons only next to visible text content — never in form fields, hidden subtrees or our own UI
const SKIPPED_CONTAINERS = [
    '[data-translator-mode-ignore]',
    '[aria-hidden="true"]',
    '[contenteditable="true"]',
    'script',
    'style',
    'noscript',
    'textarea',
    'select',
    'option',
    'title',
    'svg',
].join(', ');
const ICON_SIZE = 18;
const RESCAN_DELAY_MS = 150;

interface TranslatedText {
    node: Text;
    textBlockKey: string;
}

export interface TranslatorModeMark {
    id: number;
    textBlockKey: string;
    left: number;
    top: number;
}

const useStyles = makeStyles(() =>
    createStyles({
        layer: { position: 'fixed', top: 0, left: 0, width: 0, height: 0, zIndex: 1450, pointerEvents: 'none' },
        mark: {
            position: 'fixed',
            width: ICON_SIZE,
            height: ICON_SIZE,
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            border: '1px solid #93000b',
            background: '#fff',
            color: '#93000b',
            boxShadow: '0 1px 3px rgba(28,28,25,0.25)',
            cursor: 'pointer',
            pointerEvents: 'auto',
            '& .material-symbols-outlined': { fontSize: 12, lineHeight: 1 },
            '&:hover, &:focus-visible': { background: '#93000b', color: '#fff' },
        },
    })
);

/** The rendered text nodes that came out of i18next for a text block (recorded while the mode is on). */
export function findTranslatedTexts(root: Node): TranslatedText[] {
    const found: TranslatedText[] = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const text = node as Text;
        if (!text.data.trim()) continue;
        const textBlockKey = textBlockKeyForRenderedText(text.data);
        if (!textBlockKey || !text.parentElement || text.parentElement.closest(SKIPPED_CONTAINERS)) continue;
        found.push({ node: text, textBlockKey });
    }
    return found;
}

function isVisibleAt(element: Element, x: number, y: number, layer: Element | null): boolean {
    if (typeof document.elementsFromPoint !== 'function') return true;
    const top = document.elementsFromPoint(x, y).find(hit => !layer?.contains(hit));
    return !!top && (top === element || element.contains(top) || top.contains(element));
}

/** One icon right of the last line of each visible text; texts under a dialog or scrolled away get none. */
export function placeMarks(texts: TranslatedText[], layer: Element | null): TranslatorModeMark[] {
    const range = document.createRange();
    const marks: TranslatorModeMark[] = [];
    texts.forEach(({ node, textBlockKey }) => {
        if (!node.isConnected || !node.parentElement) return;
        range.selectNodeContents(node);
        const rects = range.getClientRects();
        if (rects.length === 0) return;
        const first = rects[0];
        const last = rects[rects.length - 1];
        if (last.width === 0 || last.height === 0) return;
        if (last.bottom < 0 || first.top > window.innerHeight || last.right < 0 || first.left > window.innerWidth)
            return;
        if (!isVisibleAt(node.parentElement, first.left + first.width / 2, first.top + first.height / 2, layer)) return;
        marks.push({
            id: marks.length,
            textBlockKey,
            left: Math.min(last.right + 2, window.innerWidth - ICON_SIZE),
            top: last.top + (last.height - ICON_SIZE) / 2,
        });
    });
    return marks;
}

const TranslatorModeLayer: React.FC<{ onEdit: (textBlockKey: string) => void }> = ({ onEdit }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const layerRef = useRef<HTMLDivElement>(null);
    const textsRef = useRef<TranslatedText[]>([]);
    const [marks, setMarks] = useState<TranslatorModeMark[]>([]);

    useEffect(() => {
        let frame = 0;
        let rescanTimer: number | undefined;
        const reposition = () => {
            if (frame) return;
            frame = window.requestAnimationFrame(() => {
                frame = 0;
                setMarks(placeMarks(textsRef.current, layerRef.current));
            });
        };
        const rescan = () => {
            if (rescanTimer !== undefined) return;
            rescanTimer = window.setTimeout(() => {
                rescanTimer = undefined;
                textsRef.current = findTranslatedTexts(document.body);
                reposition();
            }, RESCAN_DELAY_MS);
        };
        const observer = new MutationObserver(records => {
            if (records.some(record => !layerRef.current?.contains(record.target))) rescan();
        });
        observer.observe(document.body, { subtree: true, childList: true, characterData: true });
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
        document.addEventListener('transitionend', reposition, true);
        document.addEventListener('animationend', reposition, true);
        rescan();
        return () => {
            observer.disconnect();
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
            document.removeEventListener('transitionend', reposition, true);
            document.removeEventListener('animationend', reposition, true);
            if (frame) window.cancelAnimationFrame(frame);
            if (rescanTimer !== undefined) window.clearTimeout(rescanTimer);
        };
    }, []);

    return ReactDOM.createPortal(
        <div
            ref={layerRef}
            className={classes.layer}
            data-translator-mode-ignore=""
            data-testid="translator-mode-layer">
            {marks.map(mark => (
                <button
                    key={mark.id}
                    type="button"
                    className={classes.mark}
                    style={{ left: mark.left, top: mark.top }}
                    aria-label={t('account.translations.translator_mode_edit_aria', { key: mark.textBlockKey })}
                    title={mark.textBlockKey}
                    onClick={() => onEdit(mark.textBlockKey)}
                    data-testid="translator-mode-edit"
                    data-text-block-key={mark.textBlockKey}>
                    <span className="material-symbols-outlined" aria-hidden="true">
                        edit
                    </span>
                </button>
            ))}
        </div>,
        document.body
    );
};

/**
 * R49-5 Übersetzermodus host (mounted once in the App): while the mode is on and the account is a
 * translator outside the AdminPanel it draws the edit icons in a layer of its own above the page —
 * the page DOM and its layout stay untouched — and opens "Textblock bearbeiten" for the clicked key.
 * A logout, a lost translator role or a switch to the AdminPanel turns the mode off.
 */
const TranslatorModeHost: React.FC = () => {
    const on = useTranslatorMode();
    const { isLoggedIn, hasPrivilege } = useAccount();
    const location = useLocation();
    const allowed = isLoggedIn && hasPrivilege('translator') && !isAdminContext(location.pathname);
    const [editKey, setEditKey] = useState<string | null>(null);
    const closeEditor = useCallback(() => setEditKey(null), []);

    useEffect(() => {
        if (on && !allowed) void setTranslatorMode(false);
    }, [on, allowed]);

    if (!on || !allowed) return null;
    return editKey ? (
        <TextBlockEditModal blockKey={editKey} onClose={closeEditor} />
    ) : (
        <TranslatorModeLayer onEdit={setEditKey} />
    );
};

export default TranslatorModeHost;
